import type { FC } from 'react';
import React, { useRef, useState } from 'react';
import {
  MdArrowBackIosNew,
  MdOutlineAddPhotoAlternate,
  MdOutlineClose,
  MdSend,
} from 'react-icons/md';
import Swal from 'sweetalert2';

import type { MessagesInstance } from '@/lib/hooks/useMessages';

import styles from './index.module.scss';
import Message, { type MessageProps } from './Message';

const FixMessagesModal: FC<{
  title: string;
  messages: MessageProps[];
  onClose: () => void;
  fetchMore: MessagesInstance['fetchMore'];
  sendMessage: MessagesInstance['sendMessage'];
}> = ({ onClose, title, messages, fetchMore, sendMessage }) => {
  const [newMessage, setNewMessage] = useState('');
  const [currentUploadImage, setCurrentUploadImage] = useState<File | null>(
    null
  );

  const messagesRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadPreviewUrl =
    currentUploadImage && URL.createObjectURL(currentUploadImage);

  const handleSendMessage = async () => {
    if (!newMessage && !currentUploadImage) {
      return;
    }
    try {
      if (currentUploadImage) {
        await sendMessage(currentUploadImage);
        setCurrentUploadImage(null);
        const input = imageInputRef.current;
        if (input) {
          input.value = '';
        }
      }
      if (newMessage) {
        await sendMessage(newMessage);
        setNewMessage('');
      }
      if (messagesRef.current) {
        messagesRef.current.scrollTop = 0;
      }
      if (inputRef.current) {
        inputRef.current.focus();
      }
    } catch (error) {
      console.error('fix message send error', error);
      Swal.fire({
        icon: 'error',
        title: '訊息傳送失敗',
        text: error.message,
      });
    }
  };

  return (
    <div className={styles.fixMessagesModal}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={onClose}>
          <MdArrowBackIosNew />
        </button>
        <h2 className={styles.title}>{title}</h2>
      </div>

      <div
        className={styles.messages}
        ref={messagesRef}
        onScroll={(e) => {
          const nativeTarget = e.nativeEvent.target;
          if (nativeTarget instanceof HTMLDivElement) {
            const { scrollTop, scrollHeight, offsetHeight } = nativeTarget;
            const scrollDistance = scrollTop > 0 ? scrollTop : -scrollTop;
            if (scrollHeight - offsetHeight - scrollDistance < 100) {
              fetchMore();
            }
          }
        }}
      >
        {messages?.map((message) => (
          <Message key={message.id} {...message} />
        ))}
      </div>
      <div className={styles.spacer} />
      <div className={styles.newMessage}>
        <div className={styles.inputs}>
          <label htmlFor="imageInput" className={styles.imgInputLabel}>
            <input
              id="imageInput"
              type="file"
              multiple={false}
              accept="image/*"
              hidden
              ref={imageInputRef}
              onChange={(e) => {
                if (e.target.files) {
                  const file = e.target.files[0];
                  if (file.size > 1024 * 1024 * 10) {
                    e.target.value = '';
                    Swal.fire({
                      icon: 'error',
                      title: '上傳失敗',
                      text: '檔案大小超過 10MB',
                    });
                    return;
                  }
                  setCurrentUploadImage(file);
                }
              }}
            />
            <MdOutlineAddPhotoAlternate />
          </label>
          <input
            className={styles.textInput}
            ref={inputRef}
            placeholder="請輸入訊息"
            type="text"
            name="message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSendMessage();
              }
            }}
            disabled={!!uploadPreviewUrl}
          />
          <button className={styles.sendButton} onClick={handleSendMessage}>
            <MdSend />
          </button>
        </div>
        {uploadPreviewUrl && (
          <div className={styles.display}>
            <img src={uploadPreviewUrl} alt="upload image preview" />
            <button
              className={styles.removeButton}
              onClick={() => {
                setCurrentUploadImage(null);
                if (imageInputRef.current) {
                  imageInputRef.current.value = '';
                }
              }}
            >
              <MdOutlineClose />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FixMessagesModal;
